import type { ChallengeTemplate } from './types'
import { teamAbbr } from './teams'

/**
 * The twelve templates from the brief, plus Custom.
 *
 * D113: the proposition is ALWAYS side A's claim. Each game template has
 * exactly ONE `{team}` parameter, and it is the team side A is backing. If the
 * proposition comes true, side A wins. The UI labels the sides "Says yes" /
 * "Says no"; the wire format keeps a/b.
 */
export const TEMPLATES: ChallengeTemplate[] = [
  {
    id: 'first_blood',
    label: 'First Blood',
    phrasing: '{team} will be the first team to score.',
    params: [{ key: 'team', type: 'team_in_game', required: true }],
    scope: 'game', locksAtRule: 'kickoff', autoResolve: true, tieRule: 'void',
  },
  {
    id: 'next_on_board',
    label: 'Next on the Board',
    phrasing: '{team} will score next.',
    params: [{ key: 'team', type: 'team_in_game', required: true }],
    scope: 'game', locksAtRule: 'creation+60s', autoResolve: true, tieRule: 'void',
  },
  {
    id: 'opening_statement',
    label: 'Opening Statement',
    phrasing: '{team} will lead at the start of quarter {quarter}.',
    params: [
      { key: 'team', type: 'team_in_game', required: true },
      { key: 'quarter', type: 'enum', values: [2, 3, 4], required: true },
    ],
    scope: 'game', locksAtRule: 'end_of_quarter_{quarter-1}', autoResolve: true, tieRule: 'push',
  },
  {
    id: 'closing_argument',
    label: 'Closing Argument',
    phrasing: '{team} will lead at the end of quarter {quarter}.',
    params: [
      { key: 'team', type: 'team_in_game', required: true },
      { key: 'quarter', type: 'enum', values: [1, 2, 3, 4], required: true },
    ],
    scope: 'game', locksAtRule: 'start_of_quarter_{quarter}', autoResolve: true, tieRule: 'push',
  },
  {
    id: 'halftime_lead',
    label: 'Halftime Lead',
    phrasing: '{team} will be leading at halftime.',
    params: [{ key: 'team', type: 'team_in_game', required: true }],
    scope: 'game', locksAtRule: 'end_of_quarter_2', autoResolve: true, tieRule: 'push',
  },
  {
    id: 'one_team_score',
    label: 'One-Team Score',
    phrasing: '{team} will finish with exactly {points} points.',
    oppositionPhrasing: 'Any other score.',
    params: [
      { key: 'team', type: 'team_in_game', required: true },
      { key: 'points', type: 'int', min: 0, max: 99, required: true },
    ],
    scope: 'game', locksAtRule: 'kickoff', autoResolve: true, tieRule: 'none',
  },
  {
    id: 'entire_prophecy',
    label: 'The Entire Prophecy',
    phrasing: 'The final score will be {team} {scoreFor} – {opponent} {scoreAgainst}.',
    params: [
      { key: 'team', type: 'team_in_game', required: true },
      { key: 'scoreFor', type: 'int', min: 0, max: 99, required: true },
      { key: 'scoreAgainst', type: 'int', min: 0, max: 99, required: true },
    ],
    scope: 'game', locksAtRule: 'kickoff', autoResolve: true, tieRule: 'none',
  },
  {
    id: 'combined_final',
    label: 'Combined Final',
    phrasing: 'The combined final score will be {direction} {threshold}.',
    params: [
      { key: 'direction', type: 'enum', values: ['above', 'below'], required: true },
      { key: 'threshold', type: 'half_point', min: 20.5, max: 99.5, required: true },
    ],
    // half_point only accepts .5 values, so a push cannot occur (D72)
    scope: 'game', locksAtRule: 'kickoff', autoResolve: true, tieRule: 'impossible',
  },
  {
    id: 'overtime',
    label: 'Overtime',
    phrasing: 'This game will go to overtime.',
    params: [],
    scope: 'game', locksAtRule: 'kickoff', autoResolve: true, tieRule: 'none',
  },
  {
    id: 'playoffs',
    label: 'Playoffs',
    phrasing: '{team} will make the playoffs.',
    params: [{ key: 'team', type: 'any_team', required: true }],
    scope: 'season', locksAtRule: 'season_week_1_start', autoResolve: true, tieRule: 'none',
  },
  {
    id: 'super_bowl',
    label: 'Super Bowl',
    phrasing: '{team} will win the Super Bowl.',
    params: [{ key: 'team', type: 'any_team', required: true }],
    scope: 'season', locksAtRule: 'season_week_1_start', autoResolve: true, tieRule: 'none',
  },
  {
    id: 'best_in_show',
    label: 'Best in Show',
    phrasing: '{team} will finish with the best regular-season record in {scope}.',
    params: [
      { key: 'team', type: 'any_team', required: true },
      { key: 'scope', type: 'enum', values: ['the NFL', 'their conference', 'their division'], required: true },
    ],
    scope: 'season', locksAtRule: 'season_week_1_start', autoResolve: true, tieRule: 'push',
  },
  {
    id: 'custom',
    // "General challenge", not "Custom": this is the one entry on the list
    // that doesn't ask you to find a template matching what you had in mind,
    // and it is now offered first, so it has to read as the plain option
    // rather than the advanced one.
    label: 'General challenge',
    phrasing: '{text}',
    params: [{ key: 'text', type: 'string', max: 200, required: true }],
    scope: 'either', locksAtRule: 'manual', autoResolve: false, tieRule: 'manual',
  },
]

export const TEMPLATES_BY_ID: Record<string, ChallengeTemplate> =
  Object.fromEntries(TEMPLATES.map((t) => [t.id, t]))

/**
 * Render a template's sentence from its typed params. The sentence is generated
 * at display time, never stored — so a copy fix updates every historical
 * challenge (D70).
 */
export function renderProposition(
  templateId: string,
  params: Record<string, string | number>,
  game?: { homeTeamId: string; awayTeamId: string } | null,
): string {
  const t = TEMPLATES_BY_ID[templateId]
  if (!t) return 'Unknown challenge'
  if (templateId === 'custom') return String(params.text ?? '').trim() || 'Custom challenge'

  let out = t.phrasing
  for (const [key, value] of Object.entries(params)) {
    let rendered = String(value)
    if (key === 'team' || key === 'opponent') rendered = teamAbbr(String(value))
    out = out.replace(`{${key}}`, rendered)
  }
  // {opponent} is derived, not stored
  if (out.includes('{opponent}') && game && params.team) {
    const oppId = String(params.team) === game.homeTeamId ? game.awayTeamId : game.homeTeamId
    out = out.replace('{opponent}', teamAbbr(oppId))
  }
  return out.replace(/\{[^}]+\}/g, '...')
}

export function renderOpposition(templateId: string): string | null {
  return TEMPLATES_BY_ID[templateId]?.oppositionPhrasing ?? null
}

/** Human description of when a template locks, for the create flow. */
export function describeLock(templateId: string, params: Record<string, string | number>): string {
  const t = TEMPLATES_BY_ID[templateId]
  if (!t) return ''
  const q = Number(params.quarter ?? 0)
  switch (t.locksAtRule) {
    case 'kickoff': return 'Locks at kickoff.'
    case 'creation+60s': return 'Locks 60 seconds after you send it.'
    case 'end_of_quarter_2': return 'Locks at the end of Q2.'
    case 'season_week_1_start': return 'Locks at the start of Week 1.'
    case 'manual': return 'No automatic lock.'
    default:
      if (t.locksAtRule.startsWith('start_of_quarter')) return `Locks at the start of Q${q}.`
      if (t.locksAtRule.startsWith('end_of_quarter')) return `Locks at the end of Q${Math.max(1, q - 1)}.`
      return ''
  }
}

export function stakeLabel(quantity: number, unit: string, mode: 'per_person' | 'split'): string {
  return `${quantity} ${unit} ${mode === 'per_person' ? 'each way' : 'split'}`
}
